import type { AppState } from '@capacitor/app'
import { App } from '@capacitor/app'

import { RuStorePayments, type RuStoreNativePurchase, type RuStorePaymentsPlugin } from '../plugins/rustorePayments'
import { useAuthStore } from '../store/authStore'
import { useProfileStore } from '../store/profileStore'
import type { ProfileRefreshResult } from '../store/profileStore'
import type { ProfileStats } from '../types/api'
import type { PaymentChannel, PaymentResumeRelease, PlanId, PurchaseOutcome, StoreProduct, StoreProductType } from './types'

const MAX_PROFILE_ATTEMPTS = 4
const PROFILE_DELAY_MS = 750
const MAX_PENDING_ACKNOWLEDGEMENTS = 10
const ACK_STORAGE_KEY = 'rustore_pending_acknowledgements_v1'

type ResumeListener = (
  eventName: 'appStateChange',
  listener: (state: AppState) => void,
) => Promise<{ remove: () => Promise<void> | void }>
type Sleep = (milliseconds: number, signal?: AbortSignal) => Promise<void>

interface PaidPlanSnapshot {
  provider: 'tochka' | 'rustore' | null
  planId: PlanId | null
  endDate: number | null
  active: boolean
}
interface Dependencies {
  plugin?: RuStorePaymentsPlugin
  refreshProfile?: (owner: string) => Promise<ProfileRefreshResult>
  sleep?: Sleep
  storage?: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>
  expectedProducts?: Partial<Record<PlanId, string>>
  now?: () => number
  randomSuffix?: () => string
  accountKey?: () => string | null
}

export type PaymentErrorCode =
  | 'rustore_unavailable'
  | 'rustore_outdated'
  | 'rustore_unauthorized'
  | 'rustore_network_error'
  | 'rustore_product_unavailable'
  | 'purchase_rejected'
  | 'purchase_failed'
  | 'purchase_in_progress'
  | 'operation_cancelled'

export class PaymentError extends Error {
  constructor(readonly code: PaymentErrorCode) {
    super(code)
    this.name = 'PaymentError'
  }
}

export function createRustoreOrderId(now: number, randomSuffix: string): string {
  if (!Number.isSafeInteger(now) || now < 1_000_000_000_000 || now > 9_999_999_999_999) {
    throw new PaymentError('purchase_failed')
  }
  if (!/^[0-9a-f]{4}$/.test(randomSuffix)) throw new PaymentError('purchase_failed')
  return `rustore_${now}_${randomSuffix}`
}

function secureRandomSuffix(): string {
  const bytes = new Uint8Array(2)
  globalThis.crypto.getRandomValues(bytes)
  return [...bytes].map((value) => value.toString(16).padStart(2, '0')).join('')
}

function delay(milliseconds: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(Object.assign(new Error('aborted'), { name: 'AbortError' }))
      return
    }
    const timer = window.setTimeout(resolve, milliseconds)
    signal?.addEventListener('abort', () => {
      window.clearTimeout(timer)
      reject(Object.assign(new Error('aborted'), { name: 'AbortError' }))
    }, { once: true })
  })
}

function nativeCode(error: unknown): string | undefined {
  const code = (error as { code?: unknown })?.code
  return typeof code === 'string' ? code : undefined
}

function validProductId(value: unknown): value is string {
  return typeof value === 'string'
    && value.length > 0
    && value === value.trim()
    && [...value].length <= 255
    && !/\p{Cc}/u.test(value)
}

function validPurchaseId(value: unknown): value is string {
  return typeof value === 'string' && /^[A-Za-z0-9][A-Za-z0-9._-]{0,127}$/.test(value)
}

function validOwner(value: unknown): value is string {
  return typeof value === 'string' && /^[A-Za-z0-9_-]{1,128}$/.test(value)
}

function toPlanId(value: unknown): PlanId | null {
  return value === 'session' || value === 'month' || value === 'quarter' ? value : null
}

function snapshot(stats: ProfileStats): PaidPlanSnapshot {
  return {
    provider: stats.subscription_provider === 'tochka' || stats.subscription_provider === 'rustore'
      ? stats.subscription_provider
      : null,
    planId: toPlanId(stats.subscription_plan),
    endDate: typeof stats.subscription_end_date === 'number' && Number.isFinite(stats.subscription_end_date)
      ? stats.subscription_end_date
      : null,
    active: stats.subscription_status === 'active',
  }
}

function grantChanged(before: PaidPlanSnapshot, after: PaidPlanSnapshot, planId: PlanId): boolean {
  if (!after.active || after.provider !== 'rustore' || after.planId !== planId) return false
  const sameActivePlan = before.active && before.provider === 'rustore' && before.planId === planId
  if (!sameActivePlan) return true
  return after.endDate !== null && (before.endDate === null || after.endDate > before.endDate)
}

const defaultExpectedProducts: Partial<Record<PlanId, string>> = {
  session: import.meta.env.VITE_RUSTORE_SESSION_PRODUCT_ID,
  month: import.meta.env.VITE_RUSTORE_MONTH_PRODUCT_ID,
  quarter: import.meta.env.VITE_RUSTORE_QUARTER_PRODUCT_ID,
}

export class RuStorePaymentChannel implements PaymentChannel {
  readonly provider = 'rustore' as const

  private readonly plugin: RuStorePaymentsPlugin
  private readonly refreshProfile: (owner: string) => Promise<ProfileRefreshResult>
  private readonly sleep: Sleep
  private readonly storage?: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>
  private readonly expectedProducts: Partial<Record<PlanId, string>>
  private readonly now: () => number
  private readonly randomSuffix: () => string
  private readonly accountKey: () => string | null
  private purchaseFlight?: Promise<PurchaseOutcome>
  private purchaseFlightPlan?: PlanId
  private purchaseFlightOwner?: string
  private activeController?: AbortController
  private resumeListener?: Promise<{ remove: () => Promise<void> | void }>
  private resumeRefs = 0
  private disposed = false

  constructor(dependencies: Dependencies = {}) {
    this.plugin = dependencies.plugin ?? RuStorePayments
    this.refreshProfile = dependencies.refreshProfile ?? ((owner) =>
      useProfileStore.getState().refreshStatsForOwner(owner))
    this.sleep = dependencies.sleep ?? delay
    this.storage = dependencies.storage ?? (typeof localStorage === 'undefined' ? undefined : localStorage)
    this.expectedProducts = dependencies.expectedProducts ?? defaultExpectedProducts
    this.now = dependencies.now ?? Date.now
    this.randomSuffix = dependencies.randomSuffix ?? secureRandomSuffix
    this.accountKey = dependencies.accountKey ?? (() => useAuthStore.getState().userId)

    for (const productId of Object.values(this.expectedProducts)) {
      if (productId !== undefined && !validProductId(productId)) {
        throw new Error('invalid RuStore product ID configuration')
      }
    }
  }

  async getProducts(): Promise<StoreProduct[]> {
    const availability = await this.plugin.getAvailability().catch((error) => {
      throw this.mapNativeError(error)
    })
    if (!availability.available) throw this.mapNativeError({ code: availability.status })

    const { products } = await this.plugin.getProducts().catch((error) => {
      throw this.mapNativeError(error)
    })
    return products.flatMap((product) => {
      const planId = this.planForProduct(product.productId)
      return planId && this.expectedType(planId) === product.productType
        ? [{ ...product, planId }]
        : []
    })
  }

  purchase(planId: PlanId): Promise<PurchaseOutcome> {
    const owner = this.requireOwner()
    if (this.purchaseFlight) {
      return this.purchaseFlightPlan === planId && this.purchaseFlightOwner === owner
        ? this.purchaseFlight
        : Promise.reject(new PaymentError('purchase_in_progress'))
    }

    const controller = new AbortController()
    this.activeController = controller
    const flight = this.performPurchase(planId, owner, controller.signal)
    this.purchaseFlight = flight
    this.purchaseFlightPlan = planId
    this.purchaseFlightOwner = owner
    const clear = () => {
      if (this.purchaseFlight !== flight) return
      this.purchaseFlight = undefined
      this.purchaseFlightPlan = undefined
      this.purchaseFlightOwner = undefined
      this.activeController = undefined
    }
    void flight.then(clear, clear)
    return flight
  }

  async manageSubscription(): Promise<void> {
    await this.plugin.openSubscriptions().catch((error) => {
      throw this.mapNativeError(error)
    })
  }

  async retryAcknowledgements(): Promise<number> {
    const owner = this.requireOwner()
    let completed = 0
    for (const purchaseId of this.pendingAcknowledgements(owner)) {
      this.assertOwner(owner)
      try {
        await this.plugin.acknowledgePurchase({ purchaseId })
        this.assertOwner(owner)
        this.removePendingAcknowledgement(owner, purchaseId)
        completed += 1
      } catch {
        this.assertOwner(owner)
      }
    }
    return completed
  }

  async acquireAppResume(
    addListener: ResumeListener = App.addListener.bind(App),
  ): Promise<PaymentResumeRelease> {
    if (this.disposed) throw new PaymentError('operation_cancelled')
    this.resumeRefs += 1
    if (!this.resumeListener) {
      this.resumeListener = addListener('appStateChange', (state) => {
        if (!state.isActive) return
        const owner = this.currentOwner()
        if (!owner) return
        void this.retryAcknowledgements()
          .then(() => this.refreshProfile(owner))
          .catch(() => undefined)
      })
    }
    const registration = this.resumeListener
    await registration
    let released = false
    return async () => {
      if (released) return
      released = true
      this.resumeRefs = Math.max(0, this.resumeRefs - 1)
      if (this.resumeRefs !== 0 || this.resumeListener !== registration) return
      this.resumeListener = undefined
      try {
        const handle = await registration
        await handle.remove()
      } catch {
        // Listener cleanup is best effort during view teardown.
      }
    }
  }

  async dispose(): Promise<void> {
    if (this.disposed) return
    this.disposed = true
    this.activeController?.abort()
    this.resumeRefs = 0
    const registration = this.resumeListener
    this.resumeListener = undefined
    if (registration) {
      try {
        const handle = await registration
        await handle.remove()
      } catch {
        // Listener cleanup is best effort.
      }
    }
  }

  private async performPurchase(
    planId: PlanId,
    owner: string,
    signal: AbortSignal,
  ): Promise<PurchaseOutcome> {
    const productId = this.expectedProducts[planId]
    if (!validProductId(productId)) throw new PaymentError('rustore_product_unavailable')

    const initial = await this.readProfile(owner, signal)
    if (!initial) throw new PaymentError('purchase_failed')
    const orderId = createRustoreOrderId(this.now(), this.randomSuffix())

    let purchase: RuStoreNativePurchase
    try {
      purchase = await this.plugin.purchase({
        productId,
        orderId,
        developerPayload: owner,
        appUserId: owner,
      })
    } catch (error) {
      this.assertOwner(owner, signal)
      if (nativeCode(error) === 'rustore_cancelled') return { status: 'cancelled' }
      throw this.mapNativeError(error)
    }
    this.assertOwner(owner, signal)
    this.validatePurchase(productId, planId, purchase)

    for (let attempt = 0; attempt < MAX_PROFILE_ATTEMPTS; attempt += 1) {
      const updated = await this.readProfile(owner, signal)
      if (updated && grantChanged(initial, updated, planId)) {
        if (planId !== 'session') return { status: 'completed', planId }
        const purchaseId = purchase.purchaseId!
        const acknowledged = await this.acknowledgeOrRemember(owner, purchaseId, signal)
        return acknowledged
          ? { status: 'completed', planId }
          : { status: 'acknowledgement_retry', planId }
      }
      if (attempt < MAX_PROFILE_ATTEMPTS - 1) {
        try {
          await this.sleep(PROFILE_DELAY_MS, signal)
        } catch {
          throw new PaymentError('operation_cancelled')
        }
      }
    }
    return { status: 'pending', planId }
  }

  private async readProfile(
    owner: string,
    signal: AbortSignal,
  ): Promise<PaidPlanSnapshot | null> {
    this.assertOwner(owner, signal)
    const result = await this.refreshProfile(owner)
    this.assertOwner(owner, signal)
    if (result.status === 'owner_changed') throw new PaymentError('operation_cancelled')
    return result.status === 'updated' ? snapshot(result.stats) : null
  }

  private async acknowledgeOrRemember(
    owner: string,
    purchaseId: string,
    signal: AbortSignal,
  ): Promise<boolean> {
    this.assertOwner(owner, signal)
    try {
      await this.plugin.acknowledgePurchase({ purchaseId })
      this.assertOwner(owner, signal)
      this.removePendingAcknowledgement(owner, purchaseId)
      return true
    } catch {
      this.assertOwner(owner, signal)
      this.addPendingAcknowledgement(owner, purchaseId)
      return false
    }
  }

  private validatePurchase(productId: string, planId: PlanId, purchase: RuStoreNativePurchase): void {
    if (purchase.status === 'cancelled') throw new PaymentError('purchase_rejected')
    if (!validPurchaseId(purchase.purchaseId)
      || purchase.productId !== productId
      || purchase.productType !== this.expectedType(planId)) {
      throw new PaymentError('purchase_rejected')
    }
  }

  private expectedType(planId: PlanId): StoreProductType {
    return planId === 'session' ? 'CONSUMABLE_PRODUCT' : 'SUBSCRIPTION'
  }

  private planForProduct(productId: string): PlanId | undefined {
    return (Object.entries(this.expectedProducts) as Array<[PlanId, string | undefined]>)
      .find(([, expected]) => expected === productId)?.[0]
  }

  private currentOwner(): string | null {
    const value = this.accountKey()
    return validOwner(value) ? value : null
  }

  private requireOwner(): string {
    const owner = this.currentOwner()
    if (!owner || this.disposed) throw new PaymentError('operation_cancelled')
    return owner
  }

  private assertOwner(owner: string, signal?: AbortSignal): void {
    if (this.disposed || signal?.aborted || this.currentOwner() !== owner) {
      throw new PaymentError('operation_cancelled')
    }
  }

  private storageKey(owner: string): string {
    return `${ACK_STORAGE_KEY}:${encodeURIComponent(owner)}`
  }

  private pendingAcknowledgements(owner: string): string[] {
    try {
      const value: unknown = JSON.parse(this.storage?.getItem(this.storageKey(owner)) ?? '[]')
      return Array.isArray(value)
        ? [...new Set(value.filter(validPurchaseId))].slice(-MAX_PENDING_ACKNOWLEDGEMENTS)
        : []
    } catch {
      return []
    }
  }

  private addPendingAcknowledgement(owner: string, purchaseId: string): void {
    const values = [...new Set([...this.pendingAcknowledgements(owner), purchaseId])]
      .slice(-MAX_PENDING_ACKNOWLEDGEMENTS)
    try {
      this.storage?.setItem(this.storageKey(owner), JSON.stringify(values))
    } catch {
      // Access was already granted by S2S; retry persistence is best effort.
    }
  }

  private removePendingAcknowledgement(owner: string, purchaseId: string): void {
    const values = this.pendingAcknowledgements(owner).filter((item) => item !== purchaseId)
    try {
      if (values.length) this.storage?.setItem(this.storageKey(owner), JSON.stringify(values))
      else this.storage?.removeItem(this.storageKey(owner))
    } catch {
      // A stale marker is safe because acknowledgement is idempotent.
    }
  }

  private mapNativeError(error: unknown): PaymentError {
    const code = nativeCode(error)
    if (code === 'rustore_unavailable'
      || code === 'rustore_outdated'
      || code === 'rustore_unauthorized'
      || code === 'rustore_network_error'
      || code === 'rustore_product_unavailable') {
      return new PaymentError(code)
    }
    return new PaymentError('purchase_failed')
  }
}
